import { Bath, Bed } from 'lucide-react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import CurrencyAmount from '@/components/currency-amount';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { type PatchedPropertyUnit } from '@/types';

type PropertyDemarcationLegendProps = {
  property: PatchedPropertyUnit;
  height: number;
  width: number;
  selected: boolean;
  availableColor: string;
  soldColor: string;
};

export function PropertyLegend({
  property,
  height,
  width,
  selected,
  availableColor,
  soldColor,
}: PropertyDemarcationLegendProps) {
  const { t, i18n } = useTranslation();
  return (
    <div
      className="bg-white bg-opacity-80 p-4 rounded-xl shadow-md relative pointer-events-none"
      style={{
        height,
        width,
      }}
    >
      <p>
        <span className="font-medium">{property.name}</span>
      </p>
      <h1 className="font-bold">
        <CurrencyAmount className="text-3xl" amount={property.priceOfSale} currency="EUR" />
      </h1>
      <p className="mt-2 flex items-center">
        <span className="font-medium mr-1">
          {property.state === 'available'
            ? t('property.selector.legend.available')
            : t('property.selector.legend.sold')}
        </span>
        <span
          className={cn('block h-4 w-4 rounded-full')}
          style={{ backgroundColor: property.state === 'available' ? availableColor : soldColor }}
        />
      </p>
      <p className="text-2xl">
        <Bed className="inline-block" /> <span className="font-medium">{property?.bedrooms}</span>{' '}
        <span className="text-gray-800 text-base">{t('property.selector.legend.beds')}</span>
      </p>
      <p className="text-2xl mt-2">
        <Bath className="inline-block" /> <span className="font-medium">{property?.bathrooms}</span>{' '}
        <span className="text-gray-800 text-base">{t('property.selector.legend.baths')}</span>
      </p>
      <p className="text-2xl mt-2">
        {property.builtArea} <span className="text-base">m²</span>
      </p>
      {selected && (
        <div className="mt-2 flex items-center justify-center">
          <Button asChild className="pointer-events-auto">
            <Link
              href={`/${i18n.language}/project/${property.project?.slug}/property/${property.slug}`}
            >
              {t('property.selector.legend.view-property')}
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
